import { Grid8, MEDIA_ROWS, PAL_INK } from './docPix';

/**
 * The rulebook, ported from design/rulebook.html. Reference content describing
 * the Standard v1 ruleset — the live scoring always reads the round's own
 * ruleset; these numbers are the defaults, shown as prose.
 */
export function Rulebook({ onBack, onIntro }: { onBack: () => void; onIntro: () => void }) {
  return (
    <div className="doc">
      <div className="backbar">
        <button className="back" aria-label="Back" onClick={onBack}>
          ◀
        </button>
        <span className="r">THE RULES</span>
      </div>

      <header className="cover">
        <div className="bars">
          {Array.from({ length: 7 }, (_, i) => (
            <i key={i} />
          ))}
        </div>
        <h1>THIS PLACE IS A PRISON</h1>
        <div className="sub">SCORING</div>
        <div className="ed">EVERY NUMBER. NO EXCUSES.</div>
      </header>

      <nav className="toc">
        <h2>CONTENTS</h2>
        <ol>
          <li><a href="#points">Your ten points</a></li>
          <li><a href="#veto">The veto</a></li>
          <li><a href="#bonus">The most-voters bonus</a></li>
          <li><a href="#call">The call</a></li>
          <li><a href="#dupe">Two of the same</a></li>
          <li><a href="#skip">Missing a week</a></li>
          <li><a href="#boards">The three boards</a></li>
          <li><a href="#types">Round types</a></li>
        </ol>
      </nav>

      <section id="points">
        <h2>
          <span className="n">01</span> YOUR TEN POINTS
        </h2>
        <p className="lead">Ten points a round. All ten get spent, and you can't vote for yourself.</p>
        <table>
          <tbody>
            <tr>
              <th>Rule</th>
              <th />
            </tr>
            <tr><td>Points to spend</td><td className="num">10</td></tr>
            <tr><td>Most one entry can get from you</td><td className="num">+5</td></tr>
            <tr><td>Least one entry can get from you</td><td className="num">−2</td></tr>
            <tr><td>What a negative point costs you</td><td className="num">×2</td></tr>
          </tbody>
        </table>
        <div className="eg">
          <div className="h">THE DOUBLING</div>
          Marking something down costs <b>double</b>. Giving one entry <code>−2</code> uses <b>four</b> of your ten,
          leaving six for everything else. Hating things is expensive. Budget accordingly.
        </div>
      </section>

      <section id="veto">
        <h2>
          <span className="n">02</span> THE VETO
        </h2>
        <p>One veto each, every round. Costs you nothing. Name the one that should never have been submitted.</p>
        <div className="rule dark">
          <div className="lab">IF AT LEAST HALF OF YOU PICK THE SAME ONE</div>
          <div className="big">−10</div>
          <div className="note">Five of ten. Below that you were just being difficult on your own.</div>
        </div>
        <p>
          <b>You can't veto something you gave positive points to.</b> Marking it down and vetoing it is fine —
          consistent, even. Backing it and vetoing it is just cowardice.
        </p>
        <p>
          With ten players, <b>two</b> can go down in the same round. Five and five uses every veto exactly. It has to be
          a genuinely bad week.
        </p>
      </section>

      <section id="bonus">
        <h2>
          <span className="n">03</span> THE MOST-VOTERS BONUS
        </h2>
        <p>
          Whatever's backed by the most <b>separate people</b> takes a bonus. Not the highest score. The widest.
        </p>
        <div className="rule">
          <div className="lab">MOST PEOPLE GAVE IT POINTS</div>
          <div className="big">+5</div>
          <div className="note">If two or more tie for it, they take <b>+2</b> each instead.</div>
        </div>
        <div className="eg">
          <div className="h">WHY THIS MATTERS</div>
          Something getting <code>+1</code> from seven people scores 7 and takes the bonus for <b>12</b>. Something getting{' '}
          <code>+5</code> from three people scores <b>15</b> and takes nothing. Broad appeal beats three people with a
          shared problem.
        </div>
      </section>

      <section id="call">
        <h2>
          <span className="n">04</span> THE CALL
        </h2>
        <p>
          When you submit, you can <b>also</b> name the obvious one somebody is definitely going to pick. Optional, and
          blind. Nobody sees it until the reveal.
        </p>
        <div className="rule dark">
          <div className="lab">IF ANYONE ELSE SUBMITS WHAT YOU CALLED</div>
          <div className="big">+10</div>
          <div className="note">
            Pays once, however many picked it. You can't call your own, obviously. Two of you can call the same thing and
            both get paid.
          </div>
        </div>
        <p>Your call isn't an entry. It never appears in the round and nobody votes on it.</p>
      </section>

      <section id="dupe">
        <h2>
          <span className="n">05</span> TWO OF THE SAME
        </h2>
        <p>
          If two of you submit the same thing, <b>you both take −10</b>. Neither of you is as original as you thought.
        </p>
        <p>Nobody's warned at submission. You find out when voting opens and there they both are, sitting in the list, looking stupid.</p>
        <div className="rule">
          <div className="lab">A DUPLICATE IS PROTECTED</div>
          <div className="note">
            It's been punished already, so it <b>can't be marked down and can't be vetoed</b>. It can still take points,
            and it can still win the bonus, which would be a result.
          </div>
        </div>
        <p>
          The same means <b>exactly</b> the same. A different remaster or a live version is a different entry.
        </p>
      </section>

      <section id="skip">
        <h2>
          <span className="n">06</span> MISSING A WEEK
        </h2>
        <p>Don't submit and you're out. No entry, no vote, no points on any board.</p>
        <p>Nobody penalises you and nobody carries you. The round happens without you and you fall behind. Nobody is going to chase you about it either.</p>
        <p>
          <b>No entry means no vote.</b> You don't get to sit out and still ruin everyone else's week.
        </p>
      </section>

      <section id="boards">
        <h2>
          <span className="n">07</span> THE THREE BOARDS
        </h2>
        <p>Three separate things get tracked. Only the first one wins anything.</p>
        <table>
          <tbody>
            <tr>
              <th>Board</th>
              <th>What it counts</th>
            </tr>
            <tr>
              <td><b>Points</b></td>
              <td>What your entries scored, plus any calls that landed. Vetoes, duplicates and bonuses all included.</td>
            </tr>
            <tr>
              <td><b>Guessing</b></td>
              <td>How many submitters you named correctly.</td>
            </tr>
            <tr>
              <td><b>Hiding</b></td>
              <td>How many <b>failed</b> to spot yours. High is good. It means you're less predictable than you look.</td>
            </tr>
          </tbody>
        </table>
        <p>The entries are the only clue you get. No profiles, no hints, nothing.</p>
      </section>

      <section id="types">
        <h2>
          <span className="n">08</span> ROUND TYPES
        </h2>
        <p>
          <b>Scoring is identical for all three.</b> The symbol beside the theme tells you which kind of round it is.
        </p>
        <table>
          <tbody>
            <tr>
              <th>Symbol</th>
              <th>Type</th>
              <th>You submit</th>
            </tr>
            <tr>
              <td><Grid8 rows={MEDIA_ROWS.audio} palette={PAL_INK} size={18} /></td>
              <td><b>Song</b></td>
              <td>A Spotify track</td>
            </tr>
            <tr>
              <td><Grid8 rows={MEDIA_ROWS.image} palette={PAL_INK} size={18} /></td>
              <td><b>Image</b></td>
              <td>A picture, uploaded</td>
            </tr>
            <tr>
              <td><Grid8 rows={MEDIA_ROWS.video} palette={PAL_INK} size={18} /></td>
              <td><b>Video</b></td>
              <td>A YouTube link</td>
            </tr>
          </tbody>
        </table>
      </section>

      <footer className="foot">
        <p>
          THAT IS ALL OF IT.
          <br />
          EVERYTHING ELSE IS JUST ARGUING.
          <br />
          WHICH IS THE POINT.
        </p>
        <p style={{ marginTop: 14 }}>
          <a onClick={onIntro} style={{ color: 'var(--k0)', borderBottom: '2px solid var(--k2)', cursor: 'pointer' }}>
            NEW HERE? START WITH THIS
          </a>
        </p>
      </footer>
    </div>
  );
}
